import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatUserName(name: string | undefined): string {
  if (!name) return "User";
  const parts = name.split(" ");
  if (parts.length === 1) return parts[0];
  return `${parts[0][0].toUpperCase()}${parts[parts.length - 1][0].toUpperCase()}`;
}

/**
 * Format a date string or Date object as a relative time string
 * (e.g., "2 hours ago", "3 days ago", "just now")
 * Automatically uses the user's timezone and locale
 *
 * @param date - Date string (ISO 8601) or Date object
 * @returns Relative time string
 */
export function formatRelativeTime(date: string | Date): string {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  // Future dates
  if (diffMs < 0) {
    const absDiffSeconds = Math.abs(diffSeconds);
    const absDiffMinutes = Math.abs(diffMinutes);
    const absDiffHours = Math.abs(diffHours);
    const absDiffDays = Math.abs(diffDays);
    const absDiffWeeks = Math.abs(diffWeeks);
    const absDiffMonths = Math.abs(diffMonths);
    const absDiffYears = Math.abs(diffYears);

    if (absDiffSeconds < 60) return "in a few seconds";
    if (absDiffMinutes === 1) return "in 1 minute";
    if (absDiffMinutes < 60) return `in ${absDiffMinutes} minutes`;
    if (absDiffHours === 1) return "in 1 hour";
    if (absDiffHours < 24) return `in ${absDiffHours} hours`;
    if (absDiffDays === 1) return "tomorrow";
    if (absDiffDays < 7) return `in ${absDiffDays} days`;
    if (absDiffWeeks === 1) return "in 1 week";
    if (absDiffWeeks < 4) return `in ${absDiffWeeks} weeks`;
    if (absDiffMonths === 1) return "in 1 month";
    if (absDiffMonths < 12) return `in ${absDiffMonths} months`;
    if (absDiffYears === 1) return "in 1 year";
    return `in ${absDiffYears} years`;
  }

  // Past dates
  if (diffSeconds < 10) return "just now";
  if (diffSeconds < 60) return `${diffSeconds} seconds ago`;
  if (diffMinutes === 1) return "1 minute ago";
  if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
  if (diffHours === 1) return "1 hour ago";
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays === 1) return "yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffWeeks === 1) return "1 week ago";
  if (diffWeeks < 4) return `${diffWeeks} weeks ago`;
  if (diffMonths === 1) return "1 month ago";
  if (diffMonths < 12) return `${diffMonths} months ago`;
  if (diffYears === 1) return "1 year ago";
  return `${diffYears} years ago`;
}

/**
 * Format a date string or Date object with the user's locale and timezone
 * Includes both the absolute date/time and relative time
 *
 * @param date - Date string (ISO 8601) or Date object
 * @param options - Formatting options
 * @returns Formatted date string with relative time
 */
export function formatDateTime(
  date: string | Date,
  options: {
    includeTime?: boolean;
    includeRelative?: boolean;
    locale?: string;
  } = {},
): string {
  const { includeTime = true, includeRelative = true, locale } = options;
  const dateObj = typeof date === "string" ? new Date(date) : date;

  // Get user's locale from browser
  const userLocale = locale || navigator.language;

  // Format absolute date/time
  const dateOptions: Intl.DateTimeFormatOptions = includeTime
    ? {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: "short",
      }
    : {
        year: "numeric",
        month: "short",
        day: "numeric",
      };

  const absoluteDate = dateObj.toLocaleString(userLocale, dateOptions);

  if (!includeRelative) {
    return absoluteDate;
  }

  const relative = formatRelativeTime(dateObj);
  return `${relative} (${absoluteDate})`;
}

function toDisplayDate(date: string | Date): Date {
  if (typeof date === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split("-").map(Number);
      return new Date(year, month - 1, day);
    }
    return new Date(date);
  }
  return date;
}

/**
 * Format a date string as a short date (no time)
 * Uses the user's locale
 *
 * @param date - Date string (ISO 8601) or Date object
 * @returns Formatted date string
 */
export function formatDate(date: string | Date): string {
  const dateObj = toDisplayDate(date);
  const userLocale = navigator.language;

  return dateObj.toLocaleDateString(userLocale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Format a date string with weekday and long month (for tooltips and detail views).
 * Uses the same parsing rules as formatDate (calendar YYYY-MM-DD is local, not UTC midnight).
 */
export function formatDateLong(date: string | Date): string {
  return toDisplayDate(date).toLocaleDateString(navigator.language, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * Calculate the number of days remaining until a future date
 *
 * @param expirationDate - Date string (ISO 8601) or Date object
 * @returns Number of days remaining (rounded to nearest day)
 */
export function getDaysRemaining(expirationDate: string | Date): number {
  const today = new Date();
  const expiry = toDisplayDate(expirationDate);
  const diffTime = expiry.getTime() - today.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

/**
 * Format a byte count as a human-readable size (B, KB, MB, GB); "N/A" for 0 or undefined
 */
export function formatFileSize(bytes?: number): string {
  if (!bytes) return "N/A";
  const units = ["B", "KB", "MB", "GB"];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(2)} ${units[unitIndex]}`;
}
